import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import { check, type InferOutput, number, object, pipe } from "valibot";
import { dataName, errorTranslation, label, require } from "../../utils/validation/base";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { useTranslation } from "../../state/i18n";
import { createMemo } from "solid-js";
import { isEqual, isNil } from "lodash-es";
import { NUMBER_CONDITION_KEYS } from "../../constants";
import { toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { valueReferenceOfTypes } from "../../utils/validation/references";
import type { FieldType } from "../../types";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import { defaultNumberFilter } from "../../pages/customer/form/filter";
import { formatDuration } from "../../utils/timeUtil";
import { SingleSelectWithSearchInput } from "../../components/menu/SingleSelectWithSearch";
import PropertySection from "../PropertySection";
import { PreviewContentLabel } from "../FieldPreviewComponents";
import InlineMessage from "../../components/inlineMessage/InlineMessage";
import { keyFromSelector } from "i18next";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage";
import FieldValueReference from "../FieldValueReference.ts";

const ALLOWED_FIELD_TYPES: FieldType[] = ["time", "dateTime"];

const ALLOWED_VARIABLE_TYPES: VariableType[] = ["datetime"];

const Schema = pipe(
  object({
    label_text: label(),
    data_name: dataName(),
    start: require(valueReferenceOfTypes(ALLOWED_FIELD_TYPES, ALLOWED_VARIABLE_TYPES), errorTranslation(
      keyFromSelector(($) => $.VALIDATION_NO_SELECTION),
    )),
    end: require(valueReferenceOfTypes(ALLOWED_FIELD_TYPES, ALLOWED_VARIABLE_TYPES), errorTranslation(
      keyFromSelector(($) => $.VALIDATION_NO_SELECTION),
    )),
  }),
  check(
    ({ start, end }) => (!isNil(start) && !isNil(end) ? start.toString() !== end.toString() : true),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_START_EQUALS_END)),
  ),
);
const ValueSchema = number();

export type TimeDifferenceFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const TimeDifferenceField: Field<TimeDifferenceFieldProperties, DefaultDtoType, ValueType> = {
  type: "timeDifference",
  category: "dateTime",
  widget: "com.moreapps.plugins:timecalculation:4",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({ start: undefined, end: undefined }),
  duplicateProperties: (properties, updateReference) => ({
    ...properties,
    start:
      properties.start instanceof FieldValueReference
        ? (updateReference({ type: "field", value: properties.start }).value as FieldValueReference)
        : properties.start,
    end:
      properties.end instanceof FieldValueReference
        ? (updateReference({ type: "field", value: properties.end }).value as FieldValueReference)
        : properties.end,
  }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_CONDITION_KEYS, variant: "text" },
    actions: { variant: "text" },
  },
  filter: {
    type: "number",
    definition: defaultNumberFilter,
  },
  stringValue: (value) => value.toString(),
  renderValue: (value) => {
    const { t } = useTranslation();
    return formatDuration(value, t);
  },
  icon: "timeDifference",
  titleKey: keyFromSelector(($) => $.TIME_DIFFERENCE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.TIME_DIFFERENCE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    start: keyFromSelector(($) => $.FIELD_PROPERTY_START),
    end: keyFromSelector(($) => $.FIELD_PROPERTY_END),
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text}>
        <InlineMessage
          class="w-full"
          variant="informative"
          label={t(($) => $.TIME_DIFFERENCE_FIELD_PREVIEW_PLACEHOLDER)}
        />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();
    const timeFields = createMemo(() => fieldService.getFieldsByTypes(ALLOWED_FIELD_TYPES), undefined, {
      equals: (prev, next) => isEqual(prev, next),
    });
    const globalErrors = createMemo(() => props.getGlobalErrors());

    const timeOptions = createMemo(() => ({
      [t(($) => $.MENU_CATEGORY_FIELDS)]: timeFields().flatMap((field) => toFieldValueOptions(field)),
      [t(($) => $.MENU_CATEGORY_VARIABLES)]: [
        {
          [t(($) => $.MENU_SECTION_VARIABLES_CUSTOM)]: variableService
            .getVariablesByTypes(ALLOWED_VARIABLE_TYPES)
            .map(toVariableValueOption),
        },
      ],
    }));

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <SingleSelectWithSearchInput
            label={t(props.propertyKeys.start)}
            value={props.data.properties.start}
            errorMessage={props.getError("start")}
            required
            width="matchTrigger"
            options={timeOptions()}
            onSelect={(value) => props.setValue("start", value)}
          />
          <SingleSelectWithSearchInput
            label={t(props.propertyKeys.end)}
            errorMessage={props.getError("end")}
            required
            width="matchTrigger"
            value={props.data.properties.end}
            options={timeOptions()}
            onSelect={(value) => props.setValue("end", value)}
          />
          <GlobalErrorInlineMessage errors={globalErrors()} />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(TimeDifferenceField);
